/**
 * DI-04 — read the shapes on the current slide.
 *
 * Returns one entry per shape (id, Office.js type, whether it carries text,
 * the text itself) in the collection's own order, which is z-order rather than
 * reading order (PowerPoint spec §6, DI-04).
 *
 * Never throws. Every outcome is a typed {@link SlideShapesReadResult} so the
 * caller can tell "this slide is empty" from "this host can't enumerate
 * shapes" — the two cases G-8 requires to be reported differently.
 */

import { logOfficeError } from "../../../telemetry";
import { forEachShapeInGroup, GROUP_SHAPES_API_SET, isGroupShape } from "./shapeTraversal";

/** `shape.type` and `shape.textFrame` both need PowerPointApi 1.4. */
export const SLIDE_SHAPES_API_SET = { name: "PowerPointApi", version: "1.4" } as const;

export interface SlideShapeInfo {
  readonly id: string;
  /** Raw Office.js `shape.type`, e.g. `"TextBox"`. Never shown to the user. */
  readonly type: string;
  readonly hasText: boolean;
  /** The shape's text, or `""` when it has none. */
  readonly text: string;
  /**
   * Set (true) only when the shape may hold text AiVA couldn't read — a group
   * on a host below PowerPointApi 1.8, a group that failed to load, or one
   * whose only text sits deeper than the nesting cap. Lets the reply avoid
   * claiming "(no text)" about a shape that has some.
   */
  readonly textUnavailable?: true;
  /**
   * Only from {@link readCurrentSlideTextShapes}, for a shape inside a group:
   * the ids of the groups holding it, outermost first.
   */
  readonly groupPath?: readonly string[];
}

/** Separator between the texts of the shapes inside one group. */
export const GROUP_TEXT_SEPARATOR = " · ";

type ShapeTextResult = { hasText: boolean; text: string; textUnavailable?: true };

/** Why the shapes couldn't be read. Short slugs, safe for telemetry. */
export type SlideShapesErrorCode = "host_unavailable" | "api_unsupported" | "read_failed";

export type SlideShapesReadResult =
  | { readonly status: "ok"; readonly shapes: readonly SlideShapeInfo[] }
  | { readonly status: "empty" }
  | { readonly status: "no_slide" }
  | { readonly status: "unsupported"; readonly errorCode: SlideShapesErrorCode };

/**
 * Shape types that never expose a TextFrame. Office.js throws `InvalidArgument`
 * when `textFrame` is read on these, so they are not probed at all — one fewer
 * round trip per shape and no expected exceptions on a picture-heavy slide.
 * Compared case-insensitively: the enum values are PascalCase (`"Image"`).
 */
const TYPES_WITHOUT_TEXT_FRAME: ReadonlySet<string> = new Set([
  "image",
  "chart",
  "table",
  "group",
  "line",
  "media",
  "contentapp",
  "graphic",
  "ink",
  "model3d",
  "ole",
]);

/** Office.js error code for "this shape doesn't support a TextFrame". */
const INVALID_ARGUMENT = "InvalidArgument";

function pptApi(): typeof PowerPoint | undefined {
  return (globalThis as { PowerPoint?: typeof PowerPoint }).PowerPoint;
}

/** `shape.getTable()` and `table.values` need PowerPointApi 1.8. */
export const TABLE_VALUES_API_SET = { name: "PowerPointApi", version: "1.8" } as const;

/**
 * False only when the host says outright that it lacks the API set. When the
 * requirements API itself is missing we can't tell, so the read is attempted
 * and any failure is caught and reported.
 */
function isApiSetSupported(set: { name: string; version: string }): boolean {
  const requirements = (
    globalThis as {
      Office?: { context?: { requirements?: { isSetSupported?: (name: string, version: string) => boolean } } };
    }
  ).Office?.context?.requirements;
  if (typeof requirements?.isSetSupported !== "function") return true;
  return requirements.isSetSupported(set.name, set.version);
}

function isInvalidArgument(err: unknown): boolean {
  return (err as { code?: unknown } | null)?.code === INVALID_ARGUMENT;
}

function canHaveTextFrame(type: string): boolean {
  return !TYPES_WITHOUT_TEXT_FRAME.has(type.toLowerCase());
}

/**
 * Read one shape's text. A shape that turns out not to support a TextFrame is
 * an expected case and simply has no text. Anything else is a real host
 * failure: it is logged, and the shape is still listed without text so one bad
 * shape can't hide the rest of the slide.
 */
async function readShapeText(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
): Promise<ShapeTextResult> {
  if (isGroupShape(shape)) return readGroupText(context, shape);
  if (shape.type.toLowerCase() === "table") return readTableFirstRow(context, shape);
  if (!canHaveTextFrame(shape.type)) return { hasText: false, text: "" };
  try {
    const frame = shape.textFrame;
    frame.load("hasText,textRange/text");
    await context.sync();
    const text = frame.textRange?.text ?? "";
    return { hasText: frame.hasText === true && text.trim().length > 0, text };
  } catch (err) {
    if (!isInvalidArgument(err)) logOfficeError(err, "ppt-slide-shapes-text");
    return { hasText: false, text: "" };
  }
}

/**
 * A table has no TextFrame; its preview is its first row, cells joined with
 * " | " (G-8 map note: "Preview shows the first row where readable"). On a
 * host below PowerPointApi 1.8 the table is listed without text rather than
 * failing the whole slide.
 */
async function readTableFirstRow(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
): Promise<{ hasText: boolean; text: string }> {
  if (!isApiSetSupported(TABLE_VALUES_API_SET)) return { hasText: false, text: "" };
  try {
    const table = shape.getTable();
    table.load("values");
    await context.sync();
    // A header cell often wraps ("Performed\nby CB"); flatten each cell so the
    // row previews as one line instead of breaking mid-cell.
    const cells = (table.values?.[0] ?? [])
      .map((cell) => (cell ?? "").replace(/\s+/g, " ").trim())
      .filter((cell) => cell.length > 0);
    const text = cells.join(" | ");
    return { hasText: text.length > 0, text };
  } catch (err) {
    if (!isInvalidArgument(err)) logOfficeError(err, "ppt-slide-shapes-table");
    return { hasText: false, text: "" };
  }
}

/**
 * Stop reading a group's shapes once this much text is collected. The preview
 * shows 200 characters, so reading past this only costs round trips — a large
 * diagram can hold dozens of shapes, each needing its own sync.
 */
export const GROUP_TEXT_CHAR_BUDGET = 400;

const GROUP_TEXT_UNAVAILABLE: ShapeTextResult = { hasText: false, text: "", textUnavailable: true };

/**
 * A group has no TextFrame of its own; its preview is the text of the shapes
 * inside it, one flattened line per shape, joined with " · ". Nested groups
 * are opened up to the depth cap (shapeTraversal). The group stays ONE entry
 * in the list, so the shape count still matches what the user sees.
 *
 * When the text can't be read — host below PowerPointApi 1.8, a load failure,
 * or text only beyond the depth cap — the result is flagged `textUnavailable`
 * so the reply says so instead of claiming the group has no text.
 */
async function readGroupText(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
): Promise<ShapeTextResult> {
  if (!isApiSetSupported(GROUP_SHAPES_API_SET)) return GROUP_TEXT_UNAVAILABLE;
  const parts: string[] = [];
  let collected = 0;
  try {
    const { skippedDeeper } = await forEachShapeInGroup(context, shape, async (child) => {
      // Children are never groups (the walker opens those), so this reads a
      // text frame or a table. Each child's own failures are handled inside.
      const childText = await readShapeText(context, child);
      const line = childText.hasText ? childText.text.replace(/\s+/g, " ").trim() : "";
      if (line) {
        parts.push(line);
        collected += line.length + GROUP_TEXT_SEPARATOR.length;
      }
      return collected < GROUP_TEXT_CHAR_BUDGET;
    });
    if (parts.length > 0) return { hasText: true, text: parts.join(GROUP_TEXT_SEPARATOR) };
    // Nothing readable was found. If a deeper group was left unopened, its
    // text is simply unknown — don't claim the group has none.
    return skippedDeeper ? GROUP_TEXT_UNAVAILABLE : { hasText: false, text: "" };
  } catch (err) {
    if (!isInvalidArgument(err)) logOfficeError(err, "ppt-slide-shapes-group");
    return GROUP_TEXT_UNAVAILABLE;
  }
}

/** The selected slide's top-level shapes (ids and types loaded), or the outcome that ends the read. */
async function loadSlideShapes(
  context: PowerPoint.RequestContext,
): Promise<PowerPoint.Shape[] | Extract<SlideShapesReadResult, { status: "no_slide" | "empty" }>> {
  const selected = context.presentation.getSelectedSlides();
  selected.load("items/id");
  await context.sync();
  const slide = selected.items[0];
  if (!slide) return { status: "no_slide" };

  const shapes = slide.shapes;
  shapes.load("items/id,items/type");
  await context.sync();
  if (shapes.items.length === 0) return { status: "empty" };
  return shapes.items;
}

async function readWithContext(context: PowerPoint.RequestContext): Promise<SlideShapesReadResult> {
  const shapes = await loadSlideShapes(context);
  if (!Array.isArray(shapes)) return shapes;

  const out: SlideShapeInfo[] = [];
  // Sequential on purpose: a batched load fails the whole sync if a single
  // shape rejects its textFrame, which would lose every other shape's text.
  for (const shape of shapes) {
    const { hasText, text, textUnavailable } = await readShapeText(context, shape); // NOSONAR sequential on purpose, see the comment above
    out.push({ id: shape.id, type: shape.type, hasText, text, ...(textUnavailable ? { textUnavailable } : {}) });
  }
  return { status: "ok", shapes: out };
}

/**
 * Every shape inside a group, each with its own text and `groupPath`. Unlike the
 * preview there is no character budget: the rewrite needs all of the text. On a
 * failure part-way, the shapes already read are kept.
 */
async function readGroupMembers(
  context: PowerPoint.RequestContext,
  group: PowerPoint.Shape,
): Promise<SlideShapeInfo[]> {
  const members: SlideShapeInfo[] = [];
  try {
    await forEachShapeInGroup(context, group, async (child, _level, groupPath) => {
      const { hasText, text } = await readShapeText(context, child);
      members.push({ id: child.id, type: child.type, hasText, text, groupPath });
    });
  } catch (err) {
    if (!isInvalidArgument(err)) logOfficeError(err, "ppt-slide-shapes-group");
  }
  return members;
}

async function readTextShapesWithContext(context: PowerPoint.RequestContext): Promise<SlideShapesReadResult> {
  const shapes = await loadSlideShapes(context);
  if (!Array.isArray(shapes)) return shapes;

  const groupsReadable = isApiSetSupported(GROUP_SHAPES_API_SET);
  const out: SlideShapeInfo[] = [];
  // Sequential on purpose — see readWithContext.
  for (const shape of shapes) {
    if (!isGroupShape(shape)) {
      const { hasText, text } = await readShapeText(context, shape); // NOSONAR sequential, see readWithContext
      out.push({ id: shape.id, type: shape.type, hasText, text });
      continue;
    }
    // The group's own entry keeps the top-level order for the G-3 check; its text is in its members.
    if (!groupsReadable) {
      out.push({ id: shape.id, type: shape.type, hasText: false, text: "", textUnavailable: true });
      continue;
    }
    out.push({ id: shape.id, type: shape.type, hasText: false, text: "" });
    out.push(...(await readGroupMembers(context, shape))); // NOSONAR sequential on purpose, see readWithContext
  }
  return { status: "ok", shapes: out };
}

async function runSlideRead(
  read: (context: PowerPoint.RequestContext) => Promise<SlideShapesReadResult>,
): Promise<SlideShapesReadResult> {
  const api = pptApi();
  if (!api) return { status: "unsupported", errorCode: "host_unavailable" };
  if (!isApiSetSupported(SLIDE_SHAPES_API_SET)) return { status: "unsupported", errorCode: "api_unsupported" };
  try {
    return await api.run(read);
  } catch (err) {
    logOfficeError(err, "ppt-slide-shapes");
    return { status: "unsupported", errorCode: "read_failed" };
  }
}

export async function readCurrentSlideShapes(): Promise<SlideShapesReadResult> {
  return runSlideRead(readWithContext);
}

/**
 * SC-06 — like {@link readCurrentSlideShapes}, but each shape inside a group is
 * listed after the group with its own text and `groupPath`, so a rewrite can be
 * written back into it. Groups need PowerPointApi 1.8; below that they are
 * listed with `textUnavailable`.
 */
export async function readCurrentSlideTextShapes(): Promise<SlideShapesReadResult> {
  return runSlideRead(readTextShapesWithContext);
}
